import { Heading } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { BackLink, Head, LoginPassword, Promotion } from '../../../src/component'
import { Route, usePostLoginRoute } from '../../../src/config/router'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { CheckoutPage, ConsumerAccountResponse, TerminalErrorCode } from '../../../src/model'
import {
  captureCashConsent,
  setCheckoutError,
  setMobileLoginVerifiedRoute,
  useAppDispatch,
  useAppRouter,
  useIsCashApp,
  useMerchantId,
  useMobileLogin,
} from '../../../src/state'
import { phoneNumberIsCleared } from '../../../src/utils/phone-numbers'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { PromotionType } from '../../../src/component/Promotion/constants'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { CashSignInTerms } from '../../../src/component/CashSignInTerms'
import { useUpdateAfterpayJsBrand } from '../../utils/convergence'

export const PasswordPage: CheckoutPage = () => {
  useUpdateAfterpayJsBrand()
  const { t } = useTranslation()
  const router = useAppRouter()
  const { headingSize } = useCheckoutSizes()
  const { isMobileLogin, dateOfBirthVerificationRequired } = useMobileLogin()
  const merchantId = useMerchantId()
  const dispatch = useAppDispatch()
  const postLoginRoute = usePostLoginRoute()

  const pageTitle = `${t('password:welcomeBack')} | ${t('common:brandName')}`
  const isCashApp = useIsCashApp()

  useEffect(() => {
    router.prefetch(Route.ADD_MOBILE)
    router.prefetch(Route.ONE_TIME_PASSWORD)
    router.prefetch(Route.SIGN_UP)
    router.prefetch(Route.VERIFY)
    router.prefetch(Route.VERIFY_CHALLENGE)
    router.prefetch(Route.LOGGED_IN)
  }, [])

  const successHandler = async (account: ConsumerAccountResponse): Promise<void> => {
    if (!account.profileConfirmed) {
      router.push(Route.SIGN_UP)
      return
    }
    if (isCashApp) {
      dispatch(captureCashConsent())
    }
    if (phoneNumberIsCleared(account.phoneNumber ?? '')) {
      router.push(Route.ADD_MOBILE)
    } else {
      router.push(postLoginRoute, account.preferredLocale)
    }
  }

  const otpHandler = (): void => {
    if (isMobileLogin && dateOfBirthVerificationRequired) {
      dispatch(setMobileLoginVerifiedRoute(Route.ONE_TIME_PASSWORD))
      router.push(Route.VERIFY_CHALLENGE)
    } else {
      router.push(Route.ONE_TIME_PASSWORD)
    }
  }

  const declineHandler = (): void => {
    dispatch(
      setCheckoutError({
        error: TerminalErrorCode.AuthenticationDeclined,
        errorCode: ErrorCode.AuthenticationDeclined,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const forcePasswordResetRequiredHandler = (): void => {
    router.push(Route.PASSWORD_RESET_REQUIRED)
  }

  return (
    <>
      <Head title={pageTitle} />
      <BackLink backTo={Route.ENTER_EMAIL} />
      <Heading size={headingSize}>{t('password:welcomeBack')}</Heading>
      <LoginPassword
        onSuccess={successHandler}
        onOTP={otpHandler}
        onRequires2FA={() => router.push(Route.VERIFY)}
        onDeclined={declineHandler}
        onForcePasswordResetRequired={forcePasswordResetRequiredHandler}
      />
      {isCashApp && <CashSignInTerms />}
      <Promotion merchantId={merchantId} promotionType={PromotionType.NEW_USER_PROMO} />
      <TrackPageViewed />
    </>
  )
}
